import { parseISO, isSameDay } from "date-fns";

export interface ShiftForOverlap {
  id: string;
  profile_id: string | null;
  start_time: string;
  end_time: string;
  status?: string;
  employee_name?: string;
}

export interface OverlapResult {
  hasOverlap: boolean;
  overlappingShifts: ShiftForOverlap[];
}

/**
 * Check if a new shift overlaps with any existing shifts for the same employee.
 * 
 * Two shifts overlap if:
 * 1. Same profile_id (same employee)
 * 2. Same date (derived from start_time)
 * 3. Time ranges intersect: start1 < end2 AND start2 < end1
 * 4. Both shifts are active (status !== 'cancelled')
 * 
 * Open shifts (profile_id = null) do NOT overlap — they have no assigned employee.
 */
export function checkShiftOverlap(
  newShift: { profile_id: string | null; start_time: string; end_time: string },
  existingShifts: ShiftForOverlap[],
  excludeShiftId?: string // For edit mode - exclude the shift being edited
): OverlapResult {
  // Open shifts cannot overlap - no assigned employee
  if (!newShift.profile_id) {
    return { hasOverlap: false, overlappingShifts: [] };
  }

  const newStart = parseISO(newShift.start_time);
  const newEnd = parseISO(newShift.end_time);

  const overlappingShifts = existingShifts.filter((shift) => {
    // Skip if this is the shift being edited
    if (excludeShiftId && shift.id === excludeShiftId) {
      return false;
    }

    // Skip cancelled shifts
    if (shift.status === 'cancelled') {
      return false;
    }

    // Skip if different employee
    if (shift.profile_id !== newShift.profile_id) {
      return false;
    }

    const existingStart = parseISO(shift.start_time);
    const existingEnd = parseISO(shift.end_time);

    // Check if on the same day
    if (!isSameDay(newStart, existingStart)) {
      return false;
    }

    // Check for time range intersection: start1 < end2 AND start2 < end1
    const overlaps = newStart < existingEnd && existingStart < newEnd;

    return overlaps;
  });

  return {
    hasOverlap: overlappingShifts.length > 0,
    overlappingShifts,
  };
}

/**
 * Format overlapping shifts for display in the warning dialog
 */
export function formatOverlapDescription(shifts: ShiftForOverlap[]): string {
  if (shifts.length === 0) return "";
  
  return shifts
    .map((shift) => {
      const start = parseISO(shift.start_time);
      const end = parseISO(shift.end_time);
      const timeRange = `${start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
      return timeRange;
    })
    .join(", ");
}
